import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const ExportModal = ({ isOpen, onClose, onExport }) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportFields, setExportFields] = useState({
    name: true,
    date: true,
    status: true,
    location: true,
    category: true,
    ticketsSold: true,
    totalTickets: true,
    revenue: true,
    attendees: true,
    createdDate: false,
    description: false
  });
  const [dateRange, setDateRange] = useState('all');

  const formatOptions = [
    { value: 'csv', label: 'CSV (.csv)' },
    { value: 'excel', label: 'Excel (.xlsx)' },
    { value: 'pdf', label: 'PDF (.pdf)' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'All Events' },
    { value: 'this-month', label: 'This Month' },
    { value: 'last-month', label: 'Last Month' },
    { value: 'this-year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const fieldOptions = [
    { key: 'name', label: 'Event Name' },
    { key: 'date', label: 'Event Date' },
    { key: 'status', label: 'Status' },
    { key: 'location', label: 'Location' },
    { key: 'category', label: 'Category' },
    { key: 'ticketsSold', label: 'Tickets Sold' },
    { key: 'totalTickets', label: 'Total Tickets' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'attendees', label: 'Attendees' },
    { key: 'createdDate', label: 'Created Date' },
    { key: 'description', label: 'Description' }
  ];

  const handleFieldChange = (field, checked) => {
    setExportFields(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  const handleExport = () => {
    const selectedFields = Object.keys(exportFields)?.filter(key => exportFields?.[key]);
    onExport({
      format: exportFormat,
      fields: selectedFields,
      dateRange: dateRange
    });
    onClose();
  };

  const getSelectedFieldsCount = () => {
    return Object.values(exportFields)?.filter(Boolean)?.length;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-2">
              <Icon name="Download" size={20} className="text-primary" />
              <h2 className="text-lg font-semibold text-foreground">Export Events</h2>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <Icon name="X" size={16} />
            </Button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Export Format */}
            <div>
              <Select
                label="Export Format"
                options={formatOptions}
                value={exportFormat}
                onChange={setExportFormat}
              />
            </div>

            {/* Date Range */}
            <div>
              <Select
                label="Date Range"
                options={dateRangeOptions}
                value={dateRange}
                onChange={setDateRange}
              />
            </div>

            {/* Fields Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-foreground">
                  Select Fields ({getSelectedFieldsCount()})
                </label>
                <div className="flex space-x-2">
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => {
                      const allSelected = {};
                      fieldOptions?.forEach(field => {
                        allSelected[field.key] = true;
                      });
                      setExportFields(allSelected);
                    }}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => {
                      const noneSelected = {};
                      fieldOptions?.forEach(field => {
                        noneSelected[field.key] = false;
                      });
                      setExportFields(noneSelected);
                    }}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto border border-border rounded-lg p-3">
                {fieldOptions?.map((field) => (
                  <Checkbox
                    key={field?.key}
                    label={field?.label}
                    checked={exportFields?.[field?.key]}
                    onChange={(e) => handleFieldChange(field?.key, e?.target?.checked)}
                  />
                ))}
              </div>
            </div>

            {/* Export Info */}
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium mb-1">Export Information:</p>
                  <ul className="space-y-1 text-xs">
                    <li>• CSV files can be opened in Excel or Google Sheets</li>
                    <li>• PDF exports include basic formatting and charts</li>
                    <li>• Large exports may take a few moments to process</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleExport}
              disabled={getSelectedFieldsCount() === 0}
            >
              <Icon name="Download" size={16} className="mr-2" />
              Export Events
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExportModal;